/**
 * Listing readiness presentation helpers.
 *
 * Pure and dependency-free. These map the canonical readiness values produced
 * by `evaluate_listing_readiness` (persisted to `listing_readiness_checks`)
 * into operator-facing labels, tones, and next-step actions.
 *
 * Readiness itself is authoritative in the backend; this layer only presents
 * it consistently — it never recomputes readiness and never invents a
 * remediation that the source cannot determine.
 */

import { readinessFixPath } from "./listing-channel-requirements";

export type ReadinessTone = "ready" | "warning" | "blocked" | "neutral";

/**
 * Human-readable label for a listing readiness status.
 * Readiness values are stored UPPERCASE by `evaluate_listing_readiness`.
 */
export function readinessLabel(status: string | null | undefined): string {
  switch (status) {
    case "READY": return "Ready";
    case "READY_WITH_WARNINGS": return "Warnings";
    case "BLOCKED": return "Blocked";
    case "NOT_EVALUATED": return "Not evaluated";
    case "NEEDS_MAPPING": return "Needs mapping";
    default: return status ? status : "Not evaluated";
  }
}

/** Semantic tone for a readiness status, used for badge colouring. */
export function readinessTone(status: string | null | undefined): ReadinessTone {
  switch (status) {
    case "READY": return "ready";
    case "READY_WITH_WARNINGS": return "warning";
    case "BLOCKED": return "blocked";
    case "NOT_EVALUATED":
    case "NEEDS_MAPPING":
      return "neutral";
    default: return "neutral";
  }
}

/**
 * Operator-facing "next step" for a readiness check `code` emitted by
 * `evaluate_listing_readiness`. Returns `null` when no operator remediation can
 * be determined from source — the UI then shows only the check's message and
 * never a fabricated action.
 */
export function readinessConnectorLine(result: {
  connector?: { display_name?: string; auth_label?: string; authenticated?: boolean } | null;
} | null | undefined): string | null {
  const c = result?.connector;
  if (!c?.display_name) return null;
  const label = c.auth_label ?? (c.authenticated ? "Authorized" : "Not authorized");
  return `${c.display_name} — ${label}`;
}

export function readinessAction(code: string | null | undefined): string | null {
  switch (code) {
    case "connector_not_connected":
      return "Connect the channel integration";
    case "provider_no_create":
      return "Enable listing creation for this channel (integration setting)";
    case "missing_category":
      return "Select a channel category";
    case "no_variants":
      return "Add at least one variant to this listing";
    case "missing_title":
      return "Add a listing title";
    case "missing_gtin":
      return "Add GTIN/UPC/EAN only if this channel category requires it";
    case "no_media":
      return "Add a listing image";
    case "missing_brand":
      return "Add a brand to the product";
    case "missing_mpn":
      return "Add an MPN to the product";
    case "missing_weight":
      return "Add shipping weight";
    case "missing_dimensions":
      return "Add shipping dimensions";
    case "missing_variant_selling_price":
      return "Set a product or variant selling price";
    case "missing_variant_cost":
      return "Set a product or variant cost price (internal only)";
    case "missing_variant_weight":
      return "Add shipping weight";
    case "missing_variant_dimensions":
      return "Add shipping dimensions";
    case "title_too_long":
      return "Shorten the listing title to the channel limit";
    case "missing_description":
      return "Add a description for this channel";
    default:
      return null;
  }
}

export function readinessCheckAction(check: { code?: string; href?: string | null; productId?: string | null; listingId?: string | null }): string | null {
  return readinessAction(check.code);
}

export function readinessCheckHref(check: {
  code?: string;
  href?: string | null;
  product_id?: string | null;
  listing_id?: string | null;
}): string | null {
  if (check.href) return check.href;
  return readinessFixPath(check.code ?? "", check.product_id, check.listing_id);
}